import { CENT, type Money } from '@mybooks/ledger';
import { money } from '../../lib/format';
import type { Account } from '../../api/hooks';

/**
 * A report as data: the screen, the PDF preview and the CSV are all drawn from the same rows, so
 * they can never disagree.
 */
/** The prototype's variance arrow: green when better, red when worse, grey when neither. */
export interface ArrowCell { arrow: 'up' | 'down' | null; tone: 'good' | 'bad' | 'neutral' }
export type Cell = string | Money | null | ArrowCell;
export const isArrow = (c: Cell): c is ArrowCell => typeof c === 'object' && c !== null;
export type RowKind = 'sec' | 'row' | 'ind' | 'sub' | 'grand';
export interface ReportRow {
  kind: RowKind;
  /** The first cell: an account, a customer, a date. */
  label: string;
  /** Account code shown muted before the label. */
  code?: string;
  /** A second line under the label. */
  secondary?: string;
  /** Where selecting the row goes (an account's general ledger, a contact). */
  to?: string;
  /** Where the first amount links to, like the prototype's ex-link. */
  amountTo?: string;
  /** A short explanation shown as the prototype's ? dot after the label. */
  help?: string;
  /** The remaining cells. Money renders as an amount, strings as text, null as blank. */
  cells: Cell[];
  /** Muted text under a cell, by the cell's index, such as a foreign-currency amount under its base. Not in the CSV. */
  cellNotes?: Record<number, string>;
  /** On screen, the label also takes this many of the (blank) cells after it, like "Total Sales" in a date column. */
  labelSpan?: number;
}
/** arrows: a column of variance arrows, left out of the CSV and the printout as in the prototype. */
/** wrap: long text (descriptions, memos) wraps instead of widening the table. */
export interface Column { label: string; align?: 'r'; arrows?: boolean; wrap?: boolean }
export interface ReportTable {
  columns: Column[];
  rows: ReportRow[];
  /** fin: plain ledger tables. ex: statements with section headings. */
  style: 'fin' | 'ex';
  /** Show zero amounts as a dash, as aging tables do. */
  dashZero?: boolean;
  /** Extra table classes, like the prototype's at-table. */
  className?: string;
}

export const isDebitNormal = (type: string) => type === 'asset' || type === 'expense';
/** Turns a debit-positive balance into the sign that reads naturally for the account. */
export const natural = (type: string, debitPositive: Money): Money => (isDebitNormal(type) ? debitPositive : -debitPositive);

export const TYPE_NAMES: Record<Account['type'], string> = { asset: 'Assets', liability: 'Liabilities', equity: 'Equity', revenue: 'Income', expense: 'Expenses' };
export const TYPE_ONE: Record<Account['type'], string> = { asset: 'Asset', liability: 'Liability', equity: 'Equity', revenue: 'Income', expense: 'Expense' };
export const TYPES: Account['type'][] = ['asset', 'liability', 'equity', 'revenue', 'expense'];
export const SYSTEM_USE: Record<string, string> = {
  bank: 'Bank deposits and payments', undeposited_funds: 'Payments not yet deposited', accounts_receivable: 'Invoices and customer payments',
  accounts_payable: 'Bills and supplier payments', sales_tax_payable: 'Sales tax charged on invoices', customer_deposits: 'Overpayments held as customer credit',
  retained_earnings: 'Results of prior years', opening_balance_equity: 'Opening balances when you start', sales: 'Default income for invoice lines',
  cost_of_goods_sold: 'Cost of products you sell', processing_fees: 'Card processing fees',
};

/** 1234.5 → "1234.50", -3 → "-3.00": what a spreadsheet can total. Rounds half away from zero to the cent. */
export function plainAmount(value: Money): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const cents = (abs + CENT / 2n) / CENT;
  return `${negative && cents !== 0n ? '-' : ''}${cents / 100n}.${(cents % 100n).toString().padStart(2, '0')}`;
}

/** Whole-percent change from base to current, or null when there is no base to compare with. */
export function percentChange(current: Money, base: Money): bigint | null {
  if (base === 0n) return null;
  const diff = (current - base) * 100n;
  const abs = base < 0n ? -base : base;
  // Round half away from zero without floats.
  const q = diff / abs, r = diff % abs;
  return 2n * (r < 0n ? -r : r) >= abs ? q + (diff < 0n ? -1n : 1n) : q;
}

/** Variance of a figure against its base: the whole-percent change and its arrow, as the prototype's varianceCell. */
export function varianceCells(current: bigint | null, base: bigint | null, good: 'up' | 'down' | null): [Cell, ArrowCell] {
  const none: ArrowCell = { arrow: null, tone: 'neutral' };
  const p = current === null || base === null ? null : percentChange(current, base);
  if (p === null) return ['–', none];
  if (p === 0n) return ['0%', none];
  const up = p > 0n;
  return [`${p.toLocaleString('en-US')}%`, { arrow: up ? 'up' : 'down', tone: good === null ? 'neutral' : up === (good === 'up') ? 'good' : 'bad' }];
}

export const sumOf =(values: (string | null | undefined)[]): Money => values.reduce<Money>((a, v) => a + money(v), 0n);

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
/** The report as CSV: a title block, the column headings, then one line per row. Arrow columns are left out. */
export function toCsv(title: string, business: string, period: string, table: ReportTable): { csv: string; lines: number } {
  const keep = table.columns.map((c) => !c.arrows);
  const rows: string[][] = [[...table.columns.filter((_, n) => keep[n]).map((c) => c.label)]];
  for (const r of table.rows) {
    const label = r.code ? `${r.code} ${r.label}` : r.label;
    if (r.kind === 'sec') { rows.push([label]); continue; }
    const cells = r.cells.filter((_, n) => keep[n + 1] !== false);
    rows.push([label, ...cells.map((c) => (c === null || isArrow(c) ? '' : typeof c === 'bigint' ? plainAmount(c) : c))]);
  }
  const csv = [[title], [business], [period], [], ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
  return { csv, lines: rows.length };
}
export const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
